import React, { Suspense, useEffect } from 'react'
import _ from 'lodash'
import { NavigateOptions, Route, useNavigate as useRouterNavigate, useSearchParams } from 'react-router-dom'
import { PreloadBarrier } from 'mvppoker-portal-common/utils/Preload'
import Loading from './Loading'
import LoginPage from './Login/LoginPage'
import PageNotFound from './PageNotFound'
import RegistrationCredentialsPage from './Registration/Credentials/RegistrationCredentialsPage'
import RegistrationConfirmationEmailPage from './Registration/ConfirmationEmail/RegistrationConfirmationEmailPage'
import { getAllCardsUrl } from '../logic/Decks'
import { store } from '../state/AppStore'
import ForgotPasswordPage from './ForgotPassword/EnterEmail/ForgotPasswordEnterEmailPage'
import ForgotPasswordEnterCodePage from './ForgotPassword/EnterCode/ForgotPasswordEnterCodePage'
import { whenEnv, whenEnvNot } from '../config/env'
import PublicGamesPage from './Public/PublicGamesPage'
import MainPage from './Main/MainPage'
import LobbyPage from './Lobby/LobbyPage'
import PrepareGameVideoPage from './PrepageGameVideo/PrepareGameVideoPage'

const LazyGamePage = React.lazy(() => import('./Game/GamePage'))
const LazyPublicGamePage = React.lazy(() => import('./Public/PublicGamePage'))

const InviteRedirect = () => {
	const redirect = useRouterNavigate()
	const [params] = useSearchParams()

	useEffect(() => {
		redirect(`/registration/credentials?referralCode=${params.get('referralCode')}`, { replace: true })
	}, [])

	return null
}

const Routes = {
	invite: {
		path: '/invite',
		Page: () => <InviteRedirect />,
	},
	login: {
		path: '/',
		Page: () => <LoginPage />,
	},
	forgotPassword: {
		path: '/forgot-password',
		Page: () => <ForgotPasswordPage />,
	},
	forgotPasswordEnterCode: {
		path: '/forgot-password/code',
		Page: () => <ForgotPasswordEnterCodePage />,
	},
	registrationCredentials: whenEnv('prod', {
		path: '/registration/credentials',
		Page: () => <RegistrationCredentialsPage />,
	}),
	registrationConfirmationEmail: whenEnv('prod', {
		path: '/registration/confirmation/email',
		Page: () => <RegistrationConfirmationEmailPage />,
	}),
	main: {
		path: '/main',
		Page: () => <MainPage />,
	},
	lobby: {
		path: '/lobby',
		Page: () => <LobbyPage />,
	},
	prepareVideo: {
		path: '/prepare-game-video',
		Page: () => <PrepareGameVideoPage />,
	},
	game: {
		path: '/game',
		Page: () => (
			<PreloadBarrier
				images={getAllCardsUrl(store.getState().appConfig.preferredDeck)}
				whenLoading={<Loading />}
				onceDone={
					<Suspense fallback={<Loading />}>
						<LazyGamePage />
					</Suspense>
				}
			/>
		),
	},
	Fallback: {
		path: '*',
		Page: () => <PageNotFound />,
	},
	loading: {
		path: '/loading',
		Page: () => <Loading />, // React.lazy(() => import('./Loading'))
	},
	publicGames: {
		path: '/public/games',
		Page: () => <PublicGamesPage />
	},
	publicGame: {
		path: '/public/game',
		Page: () => (
			<PreloadBarrier
				images={getAllCardsUrl(0)}
				whenLoading={<Loading />}
				onceDone={
					<Suspense fallback={<Loading />}>
						<LazyPublicGamePage />
					</Suspense>
				}
			/>
		),
	},
} as const

type RoutesType = typeof Routes
type RouteKey = keyof RoutesType
type FuncArgs<K extends RouteKey> = RoutesType[K] extends { getter: any } ? Parameters<RoutesType[K]['getter']>[0] : never

type NavigateFunction<K extends keyof RoutesType> = FuncArgs<K> extends never
	? (options?: NavigateOptions) => void
	: (args: FuncArgs<K>, options?: NavigateOptions) => void

export const useNavigate = <K extends RouteKey>(key: K): NavigateFunction<K> => {
	const navigate = useRouterNavigate()
	const page = Routes[key]

	const fun = ((options?: NavigateOptions) => {
		if (!page) {
			return
		}
		navigate(page.path, options)
	}) as NavigateFunction<K>
	return fun
}

export const generateRoutes = (): JSX.Element[] => {
	const keys = Object.keys(Routes) as (keyof typeof Routes)[]
	const routes = keys.map((key) => {
		const entry = Routes[key]
		if (entry === undefined) {
			return null
		}
		const { Page, path } = entry
		return <Route path={path} element={<Page />} key={path} />
	})
	return _.compact(routes)
}
